import { Icon20Add, Icon20AdvertisingOutline, Icon20Attach } from '@vkontakte/icons';
import { ButtonGroup, Spacing, Text } from '@vkontakte/vkui';
import React, { type ReactElement, useCallback, useEffect } from 'react';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { Button } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { formatDaysLeft } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './TrialDescriptionModalMobile.css';

const ANALYTICS_CATEGORY = 'trial-tariff-cancel';

export interface TrialDescriptionModalMobileProps {
    subscription: BillingDataSubscriptionInfo;
    onClose?: () => void;
    onCancel?: () => void;
    onSuccess?: () => void;
}

export const TrialDescriptionModalMobile = (props: TrialDescriptionModalMobileProps) => {
    const { subscription, onClose, onCancel, onSuccess } = props;

    const onClick = useCallback(() => {
        sendDwh({
            eventCategory: ANALYTICS_CATEGORY,
            action: 'save',
            dwh_enabled: true,
        });
        onSuccess?.();
    }, [onSuccess]);

    const onCloseHandler = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const onSecondaryClick = useCallback(() => {
        sendDwh({
            eventCategory: ANALYTICS_CATEGORY,
            action: 'cancel',
            dwh_enabled: true,
        });
        onCancel?.();
    }, [onCancel]);

    useEffect(() => {
        sendDwh({
            eventCategory: ANALYTICS_CATEGORY,
            action: 'show',
            dwh_enabled: true,
        });
    }, []);

    const defaultMessages = {
        // eslint-disable-next-line no-irregular-whitespace
        title: `У вас ещё ${formatDaysLeft(subscription.expires)} бесплатной подписки. Отключить её?`,
        subTitle: 'Что вы потеряете, если отключите автоплатёж',
        buttonPrimary: 'Сохранить подписку',
        buttonSecondary: 'Отключить',
    };

    const featuresList: Array<{ icon: ReactElement; text: string }> = [
        {
            icon: <Icon20Add />,
            text: `Пропадёт ${subscription.space.value}`,
        },
        {
            icon: <Icon20Attach />,
            text: 'Не загрузятся файлы больше 2 ГБ',
        },
        {
            icon: <Icon20AdvertisingOutline />,
            text: 'Вернётся реклама в Облаке и Почте',
        },
    ];

    const buttons = (
        <ButtonGroup mode={'vertical'} gap={'s'} stretched>
            <Button qaId={'modal-primary-btn'} theme={'vk'} primary onClick={onClick} className={styles.button}>
                {defaultMessages.buttonPrimary}
            </Button>
            <Spacing size={5} />
            <Button qaId={'modal-secondary-btn'} theme={'vk'} secondary onClick={onSecondaryClick} className={styles.button}>
                {defaultMessages.buttonSecondary}
            </Button>
        </ButtonGroup>
    );

    const content = (
        <>
            <Text className={styles.title}>{defaultMessages.title}</Text>
            <Spacing size={12} />
            <Text className={styles.subTitle}>{defaultMessages.subTitle}</Text>
            <Spacing size={20} />
            <div className={styles.featureList}>
                {featuresList.map((feature) => (
                    <div className={styles.feature} key={feature.text}>
                        <div className={styles.icon}>{feature.icon}</div>
                        <Text className={styles.featureText}>{feature.text}</Text>
                    </div>
                ))}
            </div>
            <Spacing size={8} />
        </>
    );

    return (
        <MobileDialog
            id={'trial-description-modal-mobile'}
            open
            onClose={onCloseHandler}
            closable
            mod={'confirm'}
            topmost
            isRebrandingQuotaLanding
            dimmer
            closeOnDimmerClick
            title={<></>}
            footer={buttons}
        >
            {content}
        </MobileDialog>
    );
};

export const openTrialDescriptionModalMobile = (data?: TrialDescriptionModalMobileProps) =>
    openPopupHelper({ popupName: popupNames.TRIAL_DESCRIPTION_MODAL_MOBILE, data, onClose: data?.onClose });

export const closeTrialDescriptionModalMobile = () => closePopupHelper(popupNames.TRIAL_DESCRIPTION_MODAL_MOBILE);
